// One drawing engine for the editor preview AND the exported image, so what
// the salon sees is exactly what they post. Everything is drawn on a 1080px
// wide canvas; the editor just shows that canvas smaller.
import { stackOf } from './fonts'

export const FORMATS = {
  post: { id: 'post', w: 1080, h: 1350, label: 'Post', ratio: '4:5' },
  // Meta's safe zone for Stories and Reels: keep words out of the top 270px,
  // the bottom 670px and 65px each side
  story: { id: 'story', w: 1080, h: 1920, label: 'Story', ratio: '9:16', safe: { top: 270, bottom: 670, side: 65 } },
}

const hasLetterSpacing = typeof CanvasRenderingContext2D !== 'undefined' && 'letterSpacing' in CanvasRenderingContext2D.prototype

// "*word*" marks the accent run (the look's italic accent)
const parseRuns = (text) => {
  const out = []
  String(text || '').split(/(\*[^*]+\*)/g).forEach((part) => {
    if (!part) return
    if (/^\*[^*]+\*$/.test(part)) out.push({ t: part.slice(1, -1), accent: true })
    else out.push({ t: part, accent: false })
  })
  return out
}

// ─── Photo filters and tinted logos, worked out once per picture ───
const offscreen = (w, h) => {
  const c = document.createElement('canvas')
  c.width = w
  c.height = h
  return c
}
const clamp255 = (v) => (v < 0 ? 0 : v > 255 ? 255 : v)
// Per pixel [r, g, b] -> [r, g, b]
const FILTERS = {
  bright: (r, g, b) => [r * 1.06 + 14, g * 1.06 + 14, b * 1.06 + 12],
  warm: (r, g, b) => [r * 1.07 + 10, g * 1.02 + 4, b * 0.9],
  cool: (r, g, b) => [r * 0.92, g * 1.01 + 2, b * 1.08 + 10],
  mono: (r, g, b) => { const l = (0.299 * r + 0.587 * g + 0.114 * b - 128) * 1.08 + 128; return [l, l, l] },
}
export const PHOTO_FILTERS = [
  { id: null, name: 'Original' },
  { id: 'bright', name: 'Bright' },
  { id: 'warm', name: 'Warm' },
  { id: 'cool', name: 'Cool' },
  { id: 'mono', name: 'Mono' },
]
const filterCache = typeof WeakMap !== 'undefined' ? new WeakMap() : null
export const filteredImage = (img, filter) => {
  const fn = FILTERS[filter]
  if (!fn || !img || !img.naturalWidth || typeof document === 'undefined') return img
  const seen = filterCache && filterCache.get(img)
  if (seen && seen[filter]) return seen[filter]
  try {
    const c = offscreen(img.naturalWidth, img.naturalHeight)
    const x = c.getContext('2d')
    x.drawImage(img, 0, 0)
    const data = x.getImageData(0, 0, c.width, c.height)
    const px = data.data
    for (let i = 0; i < px.length; i += 4) {
      const [r, g, b] = fn(px[i], px[i + 1], px[i + 2])
      px[i] = clamp255(r); px[i + 1] = clamp255(g); px[i + 2] = clamp255(b)
    }
    x.putImageData(data, 0, 0)
    if (filterCache) filterCache.set(img, { ...(seen || {}), [filter]: c })
    return c
  } catch (e) {
    // A photo without CORS headers can't be read, so it stays as it is
    return img
  }
}
const logoCache = typeof WeakMap !== 'undefined' ? new WeakMap() : null
const hasClearPixels = (img) => {
  try {
    const n = 32
    const c = offscreen(n, n)
    const x = c.getContext('2d')
    x.drawImage(img, 0, 0, n, n)
    const px = x.getImageData(0, 0, n, n).data
    let clear = 0
    for (let i = 3; i < px.length; i += 4) if (px[i] < 200) clear += 1
    return clear > n * n * 0.08
  } catch (e) {
    return false
  }
}
export const tintedLogo = (img, colour) => {
  if (!img || !img.naturalWidth || typeof document === 'undefined') return img
  const seen = (logoCache && logoCache.get(img)) || {}
  if (seen.clear === undefined) seen.clear = hasClearPixels(img)
  if (!seen.clear) { if (logoCache) logoCache.set(img, seen); return img }
  if (seen[colour]) return seen[colour]
  const c = offscreen(img.naturalWidth, img.naturalHeight)
  const x = c.getContext('2d')
  x.drawImage(img, 0, 0)
  x.globalCompositeOperation = 'source-in'
  x.fillStyle = colour
  x.fillRect(0, 0, c.width, c.height)
  seen[colour] = c
  if (logoCache) logoCache.set(img, seen)
  return c
}

export const createPainter = (ctx) => {
  const boxes = []
  const font = (o, accent) => {
    const italic = accent ? o.accentItalic !== false : !!o.italic
    const weight = accent && o.accentWeight ? o.accentWeight : (o.weight || 400)
    const family = stackOf(accent && o.accentFont ? o.accentFont : o.font)
    return `${italic ? 'italic ' : ''}${weight} ${o.size}px ${family}`
  }
  const setSpacing = (px) => { if (hasLetterSpacing) ctx.letterSpacing = `${px || 0}px` }

  // Lay out text into lines of styled tokens that fit width w
  const layoutText = (text, o) => {
    const upper = !!o.upper
    const runs = parseRuns(upper ? String(text || '').toUpperCase() : text)
    const lines = [[]]
    let lineW = 0
    const space = {}
    runs.forEach((run) => {
      ctx.font = font(o, run.accent && o.accentOn !== false)
      setSpacing(o.tracking ? o.tracking * o.size : 0)
      const parts = run.t.split(/(\n| )/)
      parts.forEach((p) => {
        if (p === '\n') { lines.push([]); lineW = 0; return }
        if (p === '') return
        const tokenFont = ctx.font
        const wdt = ctx.measureText(p).width
        if (p === ' ') {
          if (lineW === 0) return
          space[tokenFont] = wdt
          lines[lines.length - 1].push({ t: ' ', w: wdt, font: tokenFont, accent: run.accent, space: true })
          lineW += wdt
          return
        }
        if (lineW > 0 && lineW + wdt > o.w) {
          // drop the trailing space before breaking
          const cur = lines[lines.length - 1]
          if (cur.length && cur[cur.length - 1].space) { lineW -= cur[cur.length - 1].w; cur.pop() }
          lines.push([]); lineW = 0
        }
        lines[lines.length - 1].push({ t: p, w: wdt, font: tokenFont, accent: run.accent })
        lineW += wdt
      })
    })
    return lines.filter((l, i) => l.length || i === 0).map(l => {
      while (l.length && l[l.length - 1].space) l.pop()
      return { tokens: l, width: l.reduce((s, t) => s + t.w, 0) }
    })
  }

  const api = {
    boxes,
    ctx,
    rect(x, y, w, h, fill, radius = 0) {
      ctx.save()
      ctx.fillStyle = fill
      api.path(x, y, w, h, radius)
      ctx.fill()
      ctx.restore()
    },
    stroke(x, y, w, h, color, width = 2, radius = 0) {
      ctx.save()
      ctx.strokeStyle = color
      ctx.lineWidth = width
      api.path(x + width / 2, y + width / 2, w - width, h - width, radius)
      ctx.stroke()
      ctx.restore()
    },
    // radius: one number, or { tl, tr, br, bl } (an arch is { tl: w / 2, tr: w / 2 })
    path(x, y, w, h, radius = 0) {
      const R = typeof radius === 'number' ? { tl: radius, tr: radius, br: radius, bl: radius } : { tl: 0, tr: 0, br: 0, bl: 0, ...radius }
      const c = (v) => Math.max(0, Math.min(v || 0, w / 2, h / 2))
      ctx.beginPath()
      ctx.moveTo(x + c(R.tl), y)
      ctx.arcTo(x + w, y, x + w, y + h, c(R.tr))
      ctx.arcTo(x + w, y + h, x, y + h, c(R.br))
      ctx.arcTo(x, y + h, x, y, c(R.bl))
      ctx.arcTo(x, y, x + w, y, c(R.tl))
      ctx.closePath()
    },
    ellipse(cx, cy, rx, ry, rotation, color, width = 2) {
      ctx.save()
      ctx.strokeStyle = color
      ctx.lineWidth = width
      ctx.beginPath()
      ctx.ellipse(cx, cy, rx, ry, rotation, 0, Math.PI * 2)
      ctx.stroke()
      ctx.restore()
    },
    line(x1, y1, x2, y2, color, width = 2, dash) {
      ctx.save()
      ctx.strokeStyle = color
      ctx.lineWidth = width
      if (dash) ctx.setLineDash(dash)
      ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke()
      ctx.restore()
    },
    circle(cx, cy, r, fill) {
      ctx.save(); ctx.fillStyle = fill; ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.fill(); ctx.restore()
    },
    stars(x, y, size, n, color, gap = 10) {
      ctx.save()
      ctx.fillStyle = color
      for (let i = 0; i < n; i += 1) {
        const cx = x + size / 2 + i * (size + gap)
        const cy = y + size / 2
        ctx.beginPath()
        for (let k = 0; k < 10; k += 1) {
          const r = k % 2 === 0 ? size / 2 : size / 4.6
          const a = -Math.PI / 2 + (k * Math.PI) / 5
          ctx.lineTo(cx + r * Math.cos(a), cy + r * Math.sin(a))
        }
        ctx.closePath()
        ctx.fill()
      }
      ctx.restore()
      return x + n * (size + gap) - gap
    },
    // Text in a box: wraps, and shrinks from o.size to o.minSize to fit
    // o.maxLines / o.maxH. Returns where it ended.
    text(text, o) {
      const opt = { weight: 400, lh: 1.2, align: 'left', ...o }
      let size = opt.size
      let lines = []
      for (;;) {
        lines = layoutText(text, { ...opt, size })
        const tooMany = opt.maxLines && lines.length > opt.maxLines
        const tooTall = opt.maxH && lines.length * size * opt.lh > opt.maxH
        const tooWide = lines.some(l => l.width > opt.w + 1)
        if ((!tooMany && !tooTall && !tooWide) || size <= (opt.minSize || size)) break
        size = Math.max(opt.minSize || size, size - Math.max(1, Math.round(size * 0.05)))
      }
      const lineH = size * opt.lh
      const total = lines.length * lineH
      // A dry run only measures (for centring a block before drawing it)
      if (opt.dry) return { top: opt.y, bottom: opt.y + total, size, lines: lines.length, width: Math.max(0, ...lines.map(l => l.width)) }
      let top = opt.y
      if (opt.valign === 'middle' && opt.maxH) top = opt.y + (opt.maxH - total) / 2
      if (opt.valign === 'bottom' && opt.maxH) top = opt.y + opt.maxH - total
      ctx.save()
      ctx.textBaseline = 'alphabetic'
      lines.forEach((line, i) => {
        let x = opt.x
        if (opt.align === 'center') x = opt.x + (opt.w - line.width) / 2
        if (opt.align === 'right') x = opt.x + opt.w - line.width
        const base = top + i * lineH + size * ((opt.lh - 1) / 2 + 0.82)
        line.tokens.forEach((tok) => {
          ctx.font = tok.font
          setSpacing(opt.tracking ? opt.tracking * size : 0)
          ctx.fillStyle = tok.accent && opt.accentColor ? opt.accentColor : opt.color
          if (!tok.space) ctx.fillText(tok.t, x, base)
          x += tok.w
        })
      })
      setSpacing(0)
      ctx.restore()
      if (opt.slot) boxes.push({ slot: opt.slot, x: opt.x, y: top, w: opt.w, h: Math.max(total, 40) })
      return { top, bottom: top + total, size, lines: lines.length, width: Math.max(0, ...lines.map(l => l.width)) }
    },
    // A logo inside a box of height h, kept to its shape. With tint, a logo
    // on a transparent background is drawn in that colour so it reads on
    // any ground (a photo-like logo is left as it is).
    logo(img, { x, y, w, h, align = 'left', tint, slot }) {
      if (!img || !img.naturalWidth) return y
      const ratio = img.naturalWidth / img.naturalHeight
      let dh = h
      let dw = dh * ratio
      const maxW = Math.min(w, 460)
      if (dw > maxW) { dw = maxW; dh = dw / ratio }
      const dx = align === 'center' ? x + (w - dw) / 2 : align === 'right' ? x + w - dw : x
      const src = tint ? tintedLogo(img, tint) : img
      ctx.drawImage(src, dx, y, dw, dh)
      if (slot) boxes.push({ slot, x: dx, y, w: dw, h: dh })
      return y + dh
    },
    // A photo that fills its box, centred on its focal point, zoomable
    image(rawImg, box, photo = {}, placeholder) {
      const { x, y, w, h, radius = 0 } = box
      const img = photo && photo.filter ? filteredImage(rawImg, photo.filter) : rawImg
      ctx.save()
      api.path(x, y, w, h, radius)
      ctx.clip()
      if (img && (img.naturalWidth || img.width)) {
        const iw = img.naturalWidth || img.width
        const ih = img.naturalHeight || img.height
        const zoom = Math.max(1, Number(photo.zoom) || 1)
        const s = Math.max(w / iw, h / ih) * zoom
        const dw = iw * s
        const dh = ih * s
        const fx = photo.fx == null ? 0.5 : photo.fx
        const fy = photo.fy == null ? 0.5 : photo.fy
        const dx = Math.min(x, Math.max(x + w - dw, x + w / 2 - fx * dw))
        const dy = Math.min(y, Math.max(y + h - dh, y + h / 2 - fy * dh))
        ctx.drawImage(img, dx, dy, dw, dh)
      } else if (placeholder) {
        ctx.fillStyle = placeholder.bg
        ctx.fillRect(x, y, w, h)
        ctx.fillStyle = placeholder.ink
        ctx.globalAlpha = 0.55
        const s = Math.min(w, h) * 0.12
        ctx.lineWidth = Math.max(3, s * 0.07)
        ctx.strokeStyle = placeholder.ink
        const cx = x + w / 2
        const cy = y + h / 2 - s * 0.35
        api.path(cx - s * 0.7, cy - s * 0.5, s * 1.4, s, s * 0.12)
        ctx.stroke()
        ctx.beginPath(); ctx.arc(cx, cy, s * 0.22, 0, Math.PI * 2); ctx.stroke()
        ctx.globalAlpha = 0.7
        ctx.font = `600 ${Math.max(30, s * 0.32)}px ${stackOf('system')}`
        ctx.textAlign = 'center'
        ctx.fillText(placeholder.label || 'Add a photo', cx, cy + s * 0.95)
      }
      ctx.restore()
      if (box.slot) boxes.push({ slot: box.slot, x, y, w, h, photo: true })
    },
    // A soft darkening from the bottom so words read over a photo
    scrim(x, y, w, h, color = '0,0,0', from = 0, to = 0.55) {
      ctx.save()
      const g = ctx.createLinearGradient(0, y, 0, y + h)
      g.addColorStop(0, `rgba(${color},${from})`)
      g.addColorStop(1, `rgba(${color},${to})`)
      ctx.fillStyle = g
      ctx.fillRect(x, y, w, h)
      ctx.restore()
    },
  }
  return api
}

// The same painter, measuring only: text returns where it would end, nothing
// is drawn and no tap targets are added
export const dryPainter = (p) => ({
  ...p,
  isDry: true,
  boxes: [],
  text: (text, o) => p.text(text, { ...o, dry: true, slot: undefined }),
  logo: (img, { y, w, h }) => {
    if (!img || !img.naturalWidth) return y
    const ratio = img.naturalWidth / img.naturalHeight
    const maxW = Math.min(w, 460)
    return y + (h * ratio > maxW ? maxW / ratio : h)
  },
  rect() {}, stroke() {}, line() {}, circle() {}, image() {}, scrim() {}, path() {}, ellipse() {},
  stars: (x, y, size, n, color, gap = 10) => x + n * (size + gap) - gap,
})

// Hex to "r,g,b" for scrims
export const rgbOf = (hex) => {
  const h = String(hex || '#000000').replace('#', '')
  const n = parseInt(h.length === 3 ? h.split('').map(c => c + c).join('') : h, 16)
  return `${(n >> 16) & 255},${(n >> 8) & 255},${n & 255}`
}
